import { useQuery, UseQueryOptions } from 'react-query';

import { RequestContext } from '@common/api-request';

import { getProductReviewSellerEventBannerByProductId } from '../graphql/event_banner_info.2';
import { GetProductReviewSellerEventBannerByProductId_ux_review_seller_event_banner_info as ProductReviewSellerEventBannerInfo } from '../graphql/event_banner_info_types.2';

export const getReviewEventBannerByProductIdKey = ({
  catalogProductId,
  catalogProductIdList,
  orderNumber,
}: {
  catalogProductId?: string;
  catalogProductIdList?: string[];
  orderNumber?: string;
}) => [
  'review-event-banner',
  'getProductReviewSellerEventBannerByProductId',
  ...(catalogProductId ? [catalogProductId] : []),
  ...(catalogProductIdList ? catalogProductIdList : []),
  ...(orderNumber ? [orderNumber] : []),
];

interface ReviewEventBannerInfoQueryProps {
  catalogProductId: string;
  orderNumber?: string;
  onlyRunning?: boolean;
  options?: QueryOptions;
}

export function useGetReviewEventBannerByProductIdQuery({
  catalogProductId,
  orderNumber,
  onlyRunning,
  options,
}: ReviewEventBannerInfoQueryProps) {
  return useQuery(
    getProductSellerEventBannerByProductIdQueryOption({
      catalogProductIdList: [catalogProductId],
      orderNumber,
      onlyRunning,
      options,
    }),
  );
}

interface RequestProductSellerEventBannerProps {
  catalogProductIdList?: string[];
  orderNumber?: string;
  onlyRunning?: boolean;
  options?: QueryOptions;
}

type QueryOptions = UseQueryOptions<ProductReviewSellerEventBannerInfo, unknown, ProductReviewSellerEventBannerInfo>;

export const getProductSellerEventBannerByProductIdQueryOption = (
  { orderNumber, catalogProductIdList, onlyRunning, options }: RequestProductSellerEventBannerProps,
  context?: RequestContext,
) => {
  const validCatalogProductIdList = validateCatalogProductIdList(catalogProductIdList);

  return {
    queryKey: getReviewEventBannerByProductIdKey(
      orderNumber
        ? { orderNumber, catalogProductIdList: validCatalogProductIdList }
        : { catalogProductIdList: validCatalogProductIdList },
    ),
    queryFn: async () => {
      const {
        data: { ux_review_seller_event_banner_info },
      } = await getProductReviewSellerEventBannerByProductId(
        {
          input: {
            product_id_list: validCatalogProductIdList,
            only_running: onlyRunning,
          },
        },
        { context, show_alert: false },
      );

      return ux_review_seller_event_banner_info;
    },
    staleTime: 60 * 1000 * 10,
    enabled: validCatalogProductIdList.length > 0,
    ...options,
  };
};

function validateCatalogProductIdList(value: string[] | undefined): string[] {
  if (!value || !Array.isArray(value)) {
    return [];
  }
  return value.filter((id): id is string => typeof id === 'string' && id.trim().length > 0).map((id) => id.trim());
}
